import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../../generic/hooks/useDateFormatter'
import { inspectionsSummary, materialsSummary } from '../../../utils/jobDetailFootprint'
import usePartFootprintDetail, { FOOTPRINT_BLOCK } from '../../../hooks/usePartFootprintDetail'
import FootprintStatusLine from './FootprintStatusLine'
import FootprintExpandableRow from './FootprintExpandableRow'
import FootprintMaterialsDetail from './FootprintMaterialsDetail'
import FootprintInspectionDetail from './FootprintInspectionDetail'

// Historia 169, enmienda UX — GATE 2 bis (UX-1, UX-2, UX-5, UX-8): la frase de estado
// (`FootprintStatusLine`) sustituye la rejilla 3×2. UX-10 (2026-09-26, decisión del PO tras revisar
// datos reales en mic1): las filas INTENTOS (D6c) y CONTEXTO (D6d) se RETIRAN de la huella — confundían
// más de lo que ayudaban; Jupiter no llama nunca a D6c ni D6d (sus componentes siguen construidos y
// testados en Sun, sin consumidor, `03-dominio.md` §"UX-10"). Quedan la línea de veredicto (UX-11, S97, en
// `FootprintStatusLine`), la frase de estado y las filas MATERIALES/INSPECCIÓN (UX-5/UX-9). Sustituye a
// `renderFootprint` de `PartsTab.jsx` (CA7: sin "Nº de serie").

const TONE_COLOR = { warning: 'yellow.main', neutral: 'text.secondary', error: 'error.light', default: 'text.primary' }

/** RN15/D4/UX-5: un resumen (materiales o inspección) en tramos "· " separados, cada uno traducido y con su tono. */
const SummaryText = ({ segments, testId }) => {
  const { t_ } = useSmartTranslation()
  return (
    <Box component='span' data-testid={testId}>
      {segments.map((segment, index) => (
        <Typography
          key={segment.key + index}
          component='span'
          sx={{ color: TONE_COLOR[segment.tone] || TONE_COLOR.default }}
        >
          {index > 0 ? ' · ' : ''}
          {t_(segment.key, segment.params)}
        </Typography>
      ))}
    </Box>
  )
}

SummaryText.propTypes = { segments: PropTypes.array, testId: PropTypes.string }

const PartFootprint = ({ data = {}, fabricationOrderId = '', jobReferenceId = '', partId = '' }) => {
  const formatter = useDateFormatter()
  const { expanded, blocks, expand, collapse, retry } = usePartFootprintDetail(fabricationOrderId, jobReferenceId, partId)

  const materials = materialsSummary(data.materials || {})
  const inspections = inspectionsSummary(data.inspections || {})

  const blockOf = (block) => blocks[block]
  const rowProps = (block) => ({
    block,
    expanded: !!expanded[block],
    status: blockOf(block).status,
    errorKey: blockOf(block).errorKey,
    onToggle: () => (expanded[block] ? collapse(block) : expand(block)),
    onRetry: () => retry(block)
  })

  return (
    <Box data-testid='part-footprint-content'>
      <FootprintStatusLine data={data} formatter={formatter} />

      <FootprintExpandableRow
        {...rowProps(FOOTPRINT_BLOCK.MATERIALS)}
        labelKey='Materials'
        summary={<SummaryText segments={materials} testId='footprint-materials' />}
      >
        <FootprintMaterialsDetail data={blockOf(FOOTPRINT_BLOCK.MATERIALS).data} />
      </FootprintExpandableRow>

      <FootprintExpandableRow
        {...rowProps(FOOTPRINT_BLOCK.INSPECTIONS)}
        labelKey='Inspection'
        summary={<SummaryText segments={inspections} testId='footprint-inspections' />}
      >
        <FootprintInspectionDetail data={blockOf(FOOTPRINT_BLOCK.INSPECTIONS).data} />
      </FootprintExpandableRow>
    </Box>
  )
}

PartFootprint.propTypes = {
  data: PropTypes.object,
  fabricationOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobReferenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  partId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default React.memo(PartFootprint)
